// Live's text presets (#241 part 2b): the ten tiles under "Add Text", each a 5 s text in its face
// at live's place (live's `constants/text-presets.ts`, read for behavior). Their places are live's
// pixels as live wrote them, the same on every frame: made for its 1080×1920 portrait.
import type { FontFamily } from "../../api/generated/model";
import type { Face } from "./fonts";
import { addedStyle, type TextBackground, type TextStyle } from "./text-style";

export interface TextPreset {
  /** Live's name for it. */
  name: string;
  /** The word its tile shows (live's preview image). */
  label: string;
  /** The text it adds. */
  text: string;
  font: FontFamily;
  weight: number;
  size: number;
  color: string;
  x: number;
  y: number;
  width: number;
  background: TextBackground | null;
  /** How its tile draws its word: a size in the panel's pixels, and Simple's dark chip. */
  tile: { size: number; chip?: boolean };
}

const WHITE = "#FFFFFF"; // raw-color: live's preset colour

export const TEXT_PRESETS: readonly TextPreset[] = [
  { name: "Classic Bold", label: "Title", text: "Title", font: "Roboto", weight: 700, size: 96, color: WHITE, x: 240, y: 898, width: 590, background: null, tile: { size: 18 } },
  {
    name: "Simple Badge",
    label: "Simple",
    text: "Simple",
    font: "Montserrat",
    weight: 700,
    size: 48,
    color: "#080808", // raw-color: live's preset colour
    x: 240,
    y: 898,
    width: 590,
    background: { color: WHITE, opacity: 1, radius: 4, padX: 24, padY: 12 },
    tile: { size: 11, chip: true },
  },
  { name: "Elegant Serif", label: "Serif", text: "Serif", font: "PT Serif", weight: 700, size: 48, color: WHITE, x: 248, y: 895, width: 582, background: null, tile: { size: 11 } },
  { name: "Monospace", label: "Typewriter", text: "Typewriter", font: "Source Code Pro", weight: 700, size: 48, color: WHITE, x: 248, y: 895, width: 582, background: null, tile: { size: 11 } },
  { name: "Poppins Bold", label: "bold", text: "Bold", font: "Poppins", weight: 700, size: 96, color: WHITE, x: 245, y: 864, width: 582, background: null, tile: { size: 18 } },
  { name: "Abril Elegant", label: "Elegant", text: "Elegant", font: "Abril Fatface", weight: 400, size: 96, color: WHITE, x: 240, y: 898, width: 590, background: null, tile: { size: 18 } },
  { name: "Modern Sora", label: "Modern", text: "Modern", font: "Sora", weight: 300, size: 96, color: WHITE, x: 240, y: 898, width: 590, background: null, tile: { size: 18 } },
  { name: "Classic Sans", label: "Classic", text: "Classic", font: "Work Sans", weight: 400, size: 96, color: WHITE, x: 240, y: 898, width: 590, background: null, tile: { size: 18 } },
  { name: "Industrial Display", label: "INDUSTRIAL", text: "INDUSTRIAL", font: "Big Shoulders Display", weight: 700, size: 96, color: WHITE, x: 240, y: 898, width: 590, background: null, tile: { size: 15 } },
  { name: "Reliable Bold", label: "Reliable", text: "Reliable", font: "Work Sans", weight: 700, size: 96, color: WHITE, x: 240, y: 898, width: 590, background: null, tile: { size: 18 } },
];

/**
 * The drag of a text tile or "Add Text" onto the timeline (issue 241): its data is the preset's
 * name, or "" for "Add Text".
 */
export const TEXT_DRAG_TYPE = "application/x-cai-text";

/** What "Add Text" adds when it is dragged rather than clicked: live's first preset, reading this. */
export const DRAGGED_TEXT = "This is a text clip";

/** The text a drag's data adds, as live's: the named preset's, or "Add Text"'s; `null` for other data. */
export function draggedText(data: string): { preset: TextPreset; text: string } | null {
  const preset = data === "" ? TEXT_PRESETS[0] : TEXT_PRESETS.find((each) => each.name === data);
  if (preset === undefined) return null;
  return { preset, text: data === "" ? DRAGGED_TEXT : preset.text };
}

/** The face a preset's tile and text draw in, for its tile's word and its text. */
export function presetFace(preset: TextPreset): Face {
  return { family: preset.font, weight: preset.weight, italic: false, text: preset.label + preset.text };
}

/** A preset's text style: live's values for it, centred, with Add Text's for the rest. */
export function presetStyle(preset: TextPreset): TextStyle {
  return {
    ...addedStyle({ width: 1080, height: 1920 }),
    x: preset.x,
    y: preset.y,
    width: preset.width,
    font: preset.font,
    weight: preset.weight,
    size: preset.size,
    align: "center",
    color: preset.color,
    background: preset.background,
  };
}
